/**
 * 스캔 실패 메시지 — **단일 출처**.
 * ============================================================
 * 설계: `docs/2026-07-07-zoolungman-coin-grant-app-dev-steps.md` §S3
 *      / 기획 `docs/2026-07-07-zoolungman-coin-grant-app.md` §4.5 답⑤·D9
 *
 * 스캔 실패는 두 갈래이며, 갈래마다 **원인 정보가 다른 곳에서 옵니다**.
 *  ① 서버 응답 실패 — `error_code` 로 판별  → `scanErrorMessage(errorCode)`
 *  ② 파서 실패(`extractQRCode` 가 null) — 서버까지 못 감. **스캔 원문**으로 판별
 *                                        → `invalidScanMessage(rawValue)`
 *
 * ⚠️ 화면마다 문구를 따로 쓰면 현장에서 같은 증상에 다른 안내가 나갑니다.
 *    새 화면은 반드시 이 모듈을 import 하십시오(문자열 직접 작성 금지).
 */

/**
 * 폐기 예정인 구 밴드 접두사.
 * ⚠️ `utils/qrUtils.js` 의 `LEGACY_BAND_PREFIX` 와 **같은 값**이지만 그쪽은 export 되지 않아
 *    여기서 별도 선언합니다. qrUtils 가 이 값을 export 하게 되면 그때 import 로 합치십시오.
 *    (S3 범위는 `QRScanner.js` 추가 1줄 + 본 신규 파일 뿐이라 qrUtils 는 손대지 않습니다.)
 */
const LEGACY_BAND_PREFIX = 'JRB_';

/**
 * API `error_code` → 스탭에게 보여줄 한글 안내.
 *
 * 근거: `api-server/src/utils/exceptions.py` (밴드 예외 :107-162, 회원/QR 예외 :30-80).
 * ⚠️ **404 를 하나로 뭉치지 않습니다** — `UNKNOWN_BAND`(DB 미등록)와 `INVALID_BAND`(형식 오류)는
 *    현장 대처가 다릅니다(전자=새 밴드 발급 필요, 후자=재스캔으로 해결 가능).
 */
export const SCAN_ERROR_MESSAGES = Object.freeze({
  // --- 밴드 5종 (기획 §4.5 답⑤) ---
  BAND_UNASSIGNED: '앱에서 밴드를 먼저 등록해 주세요', // 400 — 진짜 밴드인데 회원 미연결
  BAND_REVOKED: '폐기된 밴드입니다. 새 밴드가 필요합니다', // 409 — 분실·폐기(종착 상태)
  UNKNOWN_BAND: '등록되지 않은 밴드입니다', // 404 — 형식은 맞으나 DB 미등록(위조 포함)
  INVALID_BAND: '밴드 QR 을 다시 스캔해 주세요', // 404 — 형식 오류
  // 400 — 무인증 경로 전용 가드. 주렁맨은 인증 경로라 **미발생**이나,
  //       무음 실패를 남기지 않도록 방어적으로 매핑합니다(서버 message 와 동일 문구).
  BAND_NOT_ALLOWED_HERE: '이 화면에서는 QR밴드를 사용할 수 없습니다',

  // --- 회원 QR 3종 ---
  INVALID_QR_CODE: '인식할 수 없는 QR 입니다', // 400 — 서명 불일치 등
  MEMBER_NOT_FOUND: '등록되지 않은 회원입니다', // 404
  MEMBER_INACTIVE: '이용할 수 없는 회원입니다', // 403 — 탈퇴/정지
});

/** 매핑에 없는 `error_code`(서버 신규 예외 등) 기본 안내 — 무음 실패 방지용. */
export const DEFAULT_SCAN_ERROR_MESSAGE = '처리 중 오류가 발생했습니다. 다시 시도해 주세요';

/** 파싱 실패 + 원문에 `JRB_` 포함 → 구 밴드. 재스캔해도 안 되므로 교체를 안내합니다. */
export const LEGACY_BAND_SCAN_MESSAGE = '구형 주렁밴드입니다. 교체가 필요합니다';

/** 파싱 실패 + 그 외 → 무엇을 비춰야 하는지까지 알려줍니다. */
export const UNPARSABLE_SCAN_MESSAGE =
  '인식할 수 없는 QR 입니다. 회원 QR 또는 주렁밴드를 비춰주세요';

/**
 * API 에러 응답의 `error_code` 를 한글 안내로 바꿉니다.
 * 미지의 코드·빈값·비문자열은 전부 기본 메시지로 흡수합니다(화면 무반응 금지).
 *
 * @param {string} errorCode 서버 응답의 error_code
 * @returns {string} 항상 비어있지 않은 안내 문자열
 */
export const scanErrorMessage = (errorCode) => {
  if (typeof errorCode !== 'string' || !errorCode) return DEFAULT_SCAN_ERROR_MESSAGE;
  // ⚠️ `SCAN_ERROR_MESSAGES[errorCode]` 직접 접근 금지 — 'toString'·'constructor' 같은
  //    Object.prototype 키가 함수로 새어 나옵니다. 자기 키만 인정합니다.
  if (!Object.prototype.hasOwnProperty.call(SCAN_ERROR_MESSAGES, errorCode)) {
    return DEFAULT_SCAN_ERROR_MESSAGE;
  }
  return SCAN_ERROR_MESSAGES[errorCode];
};

/**
 * 파서(`extractQRCode`)가 `null` 을 돌려준 스캔의 안내를 만듭니다 — D9.
 * 서버에 가지 못한 실패라 `error_code` 가 없으므로 **스캔 원문**으로 구분합니다.
 *
 * @param {string} rawValue QR 스캔 원문(`QRScanner` 의 `onInvalidScan` 인자)
 * @returns {string} 항상 비어있지 않은 안내 문자열
 */
export const invalidScanMessage = (rawValue) => {
  if (typeof rawValue !== 'string' || !rawValue) return UNPARSABLE_SCAN_MESSAGE;
  // raw(`JRB_...`)도 URL(`https://.../bands/JRB_...`)도 잡아야 하므로 includes 로 봅니다.
  if (rawValue.includes(LEGACY_BAND_PREFIX)) return LEGACY_BAND_SCAN_MESSAGE;
  return UNPARSABLE_SCAN_MESSAGE;
};
